// Appearance (DRAW_PLAN §1): the object's opacity and blend, then its fills and strokes — several of each,
// each with colour, opacity and blend; strokes with width, cap, join, miter limit, dashes, alignment and
// arrowheads. Edits apply to the selection, or to the current appearance when nothing is selected.
import { X } from 'lucide-react';
import { useState } from 'react';
import { useT, type MessageKey } from '../../../i18n';
import { useDesign } from '../../state';
import { useDesignUi } from '../designUi';
import { editLook, shownAppearance } from '../look';
import { ColorPicker } from '../ColorPicker';
import { cssColor } from '../../render/paint';
import { BLACK, WHITE, defaultStroke } from '../../model/defaults';
import type { ArrowId, BlendMode, ColorRef, Paint } from '../../model/types';

export const BLENDS: BlendMode[] = [
  'normal',
  'multiply',
  'screen',
  'overlay',
  'darken',
  'lighten',
  'color-dodge',
  'color-burn',
  'hard-light',
  'soft-light',
  'difference',
  'exclusion',
  'hue',
  'saturation',
  'color',
  'luminosity',
];
const BLEND_KEY: Record<BlendMode, MessageKey> = {
  normal: 'blendNormal',
  multiply: 'blendMultiply',
  screen: 'blendScreen',
  overlay: 'blendOverlay',
  darken: 'blendDarken',
  lighten: 'blendLighten',
  'color-dodge': 'blendColorDodge',
  'color-burn': 'blendColorBurn',
  'hard-light': 'blendHardLight',
  'soft-light': 'blendSoftLight',
  difference: 'blendDifference',
  exclusion: 'blendExclusion',
  hue: 'blendHue',
  saturation: 'blendSaturation',
  color: 'blendColor',
  luminosity: 'blendLuminosity',
};
const ARROWS: ArrowId[] = ['none', 'triangle', 'open', 'circle', 'square', 'bar'];
const ARROW_KEY: Record<ArrowId, MessageKey> = {
  none: 'arrowNone',
  triangle: 'arrowTriangle',
  open: 'arrowOpen',
  circle: 'arrowCircle',
  square: 'arrowSquare',
  bar: 'arrowBar',
};

let drags = 0;
const merge = () => `look-${drags}`;
const endDrag = () => {
  drags++;
  useDesign.getState().endGesture();
};

export function BlendSelect({
  value,
  onChange,
  testid,
}: {
  value: BlendMode;
  onChange: (b: BlendMode) => void;
  testid: string;
}) {
  const t = useT();
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value as BlendMode)}
      aria-label={t('blend')}
    >
      {BLENDS.map((b) => (
        <option key={b} value={b}>
          {t(BLEND_KEY[b])}
        </option>
      ))}
    </select>
  );
}

function Percent({
  value,
  onChange,
  testid,
  label,
}: {
  value: number;
  onChange: (v: number, final: boolean) => void;
  testid: string;
  label: string;
}) {
  return (
    <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {label}
      <input
        type="range"
        min={0}
        max={100}
        data-testid={testid}
        value={Math.round(value * 100)}
        onChange={(e) => onChange(Number(e.target.value) / 100, false)}
        onPointerUp={endDrag}
        onKeyUp={endDrag}
        style={{ width: 90 }}
      />
      <span style={{ width: 34 }}>{Math.round(value * 100)}%</span>
    </label>
  );
}

/** A colour chip that opens the picker under it. */
export function PaintChip({
  paint,
  onChange,
  testid,
}: {
  paint: Paint;
  onChange: (c: ColorRef | null, final: boolean) => void;
  testid: string;
}) {
  const t = useT();
  const doc = useDesign((st) => st.doc);
  const [open, setOpen] = useState(false);
  const color = paint.kind === 'solid' ? paint.color : null;
  return (
    <span style={{ display: 'inline-block' }}>
      <button
        data-testid={testid}
        aria-label={t('color')}
        onClick={() => setOpen(!open)}
        style={{
          width: 26,
          height: 20,
          padding: 0,
          background: color
            ? cssColor(color, doc)
            : 'linear-gradient(to top right, var(--pure-white) 45%, var(--none-slash) 47%, var(--none-slash) 53%, var(--pure-white) 55%)',
          border: '1px solid var(--swatch-border)',
        }}
      />
      {open && (
        <div
          style={{
            position: 'relative',
            zIndex: 5,
            background: 'var(--panel)',
            padding: 6,
            border: '1px solid var(--line)',
            marginTop: 4,
          }}
        >
          <ColorPicker
            color={color ?? BLACK}
            cmyk={doc?.colorMode === 'cmyk'}
            onChange={(c, f) => onChange(c, f)}
            testid={`${testid}-picker`}
          />
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            <button onClick={() => onChange(null, true)}>{t('noneColor')}</button>
            <button onClick={() => setOpen(false)}>{t('close')}</button>
          </div>
        </div>
      )}
    </span>
  );
}

export function AppearanceDock() {
  const t = useT();
  useDesign((st) => st.version);
  useDesignUi((st) => st.selection);
  useDesignUi((st) => st.current);
  const a = shownAppearance();
  const solid = (c: ColorRef | null): Paint => (c ? { kind: 'solid', color: c } : { kind: 'none' });
  return (
    <div data-testid="appearance-dock" style={{ display: 'grid', gap: 8 }}>
      <section style={{ display: 'grid', gap: 4 }}>
        <strong>{t('objectLook')}</strong>
        <Percent
          label={t('opacity')}
          testid="ap-opacity"
          value={a.opacity}
          onChange={(v) => editLook((x) => void (x.opacity = v), merge())}
        />
        <BlendSelect
          testid="ap-blend"
          value={a.blend}
          onChange={(b) => editLook((x) => void (x.blend = b))}
        />
      </section>
      <section style={{ display: 'grid', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <strong style={{ flex: 1 }}>{t('fills')}</strong>
          <button
            data-testid="ap-add-fill"
            onClick={() =>
              editLook(
                (x) =>
                  void x.fills.push({ paint: { kind: 'solid', color: WHITE }, opacity: 1, blend: 'normal' }),
              )
            }
          >
            +
          </button>
        </div>
        {a.fills.map((f, i) => (
          <div
            key={i}
            style={{ display: 'grid', gap: 3, paddingLeft: 6, borderLeft: '2px solid var(--line)' }}
          >
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <PaintChip
                testid={`ap-fill-${i}`}
                paint={f.paint}
                onChange={(c, fin) =>
                  editLook((x) => void (x.fills[i]!.paint = solid(c)), fin ? undefined : merge())
                }
              />
              <BlendSelect
                testid={`ap-fill-${i}-blend`}
                value={f.blend}
                onChange={(b) => editLook((x) => void (x.fills[i]!.blend = b))}
              />
              <button aria-label={t('remove')} onClick={() => editLook((x) => void x.fills.splice(i, 1))}>
                <X className="icon" aria-hidden="true" />
              </button>
            </div>
            <Percent
              label={t('opacity')}
              testid={`ap-fill-${i}-opacity`}
              value={f.opacity}
              onChange={(v) => editLook((x) => void (x.fills[i]!.opacity = v), merge())}
            />
          </div>
        ))}
      </section>
      <section style={{ display: 'grid', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <strong style={{ flex: 1 }}>{t('strokes')}</strong>
          <button
            data-testid="ap-add-stroke"
            onClick={() => editLook((x) => void x.strokes.push(defaultStroke()))}
          >
            +
          </button>
        </div>
        {a.strokes.map((s, i) => (
          <div
            key={i}
            style={{ display: 'grid', gap: 3, paddingLeft: 6, borderLeft: '2px solid var(--line)' }}
          >
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <PaintChip
                testid={`ap-stroke-${i}`}
                paint={s.paint}
                onChange={(c, fin) =>
                  editLook((x) => void (x.strokes[i]!.paint = solid(c)), fin ? undefined : merge())
                }
              />
              <label style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {t('strokeWidth')}
                <input
                  type="number"
                  min={0}
                  step="any"
                  data-testid={`ap-stroke-${i}-width`}
                  key={`w${s.width}`}
                  defaultValue={s.width}
                  style={{ width: 52 }}
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isFinite(v) && v >= 0 && v !== s.width)
                      editLook((x) => void (x.strokes[i]!.width = v));
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                />
              </label>
              <button aria-label={t('remove')} onClick={() => editLook((x) => void x.strokes.splice(i, 1))}>
                <X className="icon" aria-hidden="true" />
              </button>
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <select
                data-testid={`ap-stroke-${i}-cap`}
                aria-label={t('strokeCap')}
                value={s.cap}
                onChange={(e) => editLook((x) => void (x.strokes[i]!.cap = e.target.value as typeof s.cap))}
              >
                <option value="butt">{t('capButt')}</option>
                <option value="round">{t('capRound')}</option>
                <option value="square">{t('capSquare')}</option>
              </select>
              <select
                data-testid={`ap-stroke-${i}-join`}
                aria-label={t('strokeJoin')}
                value={s.join}
                onChange={(e) => editLook((x) => void (x.strokes[i]!.join = e.target.value as typeof s.join))}
              >
                <option value="miter">{t('joinMiter')}</option>
                <option value="round">{t('joinRound')}</option>
                <option value="bevel">{t('joinBevel')}</option>
              </select>
              <select
                data-testid={`ap-stroke-${i}-align`}
                aria-label={t('strokeAlign')}
                value={s.align}
                onChange={(e) =>
                  editLook((x) => void (x.strokes[i]!.align = e.target.value as typeof s.align))
                }
              >
                <option value="center">{t('alignCenter')}</option>
                <option value="inside">{t('alignInside')}</option>
                <option value="outside">{t('alignOutside')}</option>
              </select>
            </div>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              {t('miterLimit')}
              <input
                type="number"
                min={1}
                step="any"
                key={`m${s.miter}`}
                defaultValue={s.miter}
                style={{ width: 52 }}
                onBlur={(e) =>
                  editLook((x) => void (x.strokes[i]!.miter = Math.max(1, Number(e.target.value) || 10)))
                }
              />
            </label>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              {t('dashes')}
              <input
                data-testid={`ap-stroke-${i}-dash`}
                placeholder="6 3"
                key={`d${s.dash.join(' ')}`}
                defaultValue={s.dash.join(' ')}
                style={{ width: 80 }}
                onBlur={(e) => {
                  const d = e.target.value
                    .split(/[\s,]+/)
                    .map(Number)
                    .filter((v) => Number.isFinite(v) && v >= 0);
                  editLook((x) => void (x.strokes[i]!.dash = d.some((v) => v > 0) ? d : []));
                }}
                onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
              />
              <input
                type="number"
                step="any"
                aria-label={t('dashOffset')}
                key={`o${s.dashOffset}`}
                defaultValue={s.dashOffset}
                style={{ width: 46 }}
                onBlur={(e) => editLook((x) => void (x.strokes[i]!.dashOffset = Number(e.target.value) || 0))}
              />
            </label>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              {t('arrows')}
              {(['startArrow', 'endArrow'] as const).map((k) => (
                <select
                  key={k}
                  data-testid={`ap-stroke-${i}-${k}`}
                  aria-label={t(k === 'startArrow' ? 'arrowStart' : 'arrowEnd')}
                  value={s[k] ?? 'none'}
                  onChange={(e) => editLook((x) => void (x.strokes[i]![k] = e.target.value as ArrowId))}
                >
                  {ARROWS.map((ar) => (
                    <option key={ar} value={ar}>
                      {t(ARROW_KEY[ar])}
                    </option>
                  ))}
                </select>
              ))}
            </div>
            <Percent
              label={t('opacity')}
              testid={`ap-stroke-${i}-opacity`}
              value={s.opacity}
              onChange={(v) => editLook((x) => void (x.strokes[i]!.opacity = v), merge())}
            />
            <BlendSelect
              testid={`ap-stroke-${i}-blend`}
              value={s.blend}
              onChange={(b) => editLook((x) => void (x.strokes[i]!.blend = b))}
            />
          </div>
        ))}
      </section>
    </div>
  );
}
